import Image from 'next/image';
import { CheckmarkIcon, QuoteIcon } from '@/icons';
import { BesqabLogotype, SBABLogotype } from '@/illustrations';
import ObosLogotype from '@/illustrations/ClientLogotypes/ObosLogotype';

interface ITestimonial {
  testimonial: string;
  name: string;
  role: string;
  company: string;
}

const Testimonials = [
  {
    testimonial:
      'Jag använder Booli Pro som en del av intagsargument. Ett väldigt bra verktyg för bostäder/föreningar i områden där priserna kan skilja ganska mycket beroende på vilken förening det är. Uppskattar främst BRF-rapporterna som gör att vi kan presentera ett material mot kund som visar på jämförelser och nyckeltal.',
    name: 'Nina Berglund',
    role: 'Fastighetsmäklare, ansvarig affärsutveckling & partner',
    company: 'CROISETTE',
  },
  {
    testimonial:
      'Booli Pro hjälper oss när vi gör marknadsanalyser för våra planerade projekt. Graferna i verktyget går enkelt att anpassa efter behov. Bl a kan man se volymer, säljtakt och prisutveckling över tid och per bostadsstorlek. Vi får alltid väldigt snabb respons på frågor om verktyget.',
    name: 'Karin Tempelman',
    role: 'Analytiker',
    company: 'RIKSBYGGEN',
  },
  {
    testimonial:
      'När jag arbetar med prissättning av våra bostäder har jag stor användning av data från andra nyproduktionsprojekt, till exempel inom samma geografiska områden eller andra jämförbara områden. En annan funktion jag använder är att kunna följa utvecklingen på marknaden. Jag uppskattar också möjligheten att data går att arbeta vidare med i Excel.',
    name: 'Malin Castenvik',
    role: 'Försäljningschef',
    company: 'BESQAB',
  },
  {
    testimonial:
      'Vi använder Booli Pro regelbundet, dels tittar vi på marknaden totalt sett men framför allt använder vi verktyget för att få en prisuppfattning i lokala områden när vi själva ska prissätta våra projekt.Vi uppskattar Boolis aktiva dialog med oss när det gäller önskemål och utveckling.',
    name: 'Gunilla Slättås, Anette Högberg',
    role: 'Försäljningschef, Försäljningsansvarig',
    company: 'OBOS',
  },
  {
    testimonial:
      'Booli Pro är en ovärderlig datakälla för den som är intresserad av att följa både det aktuella läget på bostadsmarknaden och olika trender. Jag använder Booli Pro varje dag i mitt arbete som chefsekonom på SBAB. Varje gång jag är ute och talar om svensk bostadsmarknad använder jag grafer hämtade från Booli Pro. I mina ögon är Booli Pro en riktig guldgruva!',
    name: 'Robert Boije',
    role: 'Chefsekonom',
    company: 'SBAB',
  },
  {
    testimonial:
      'Vi använder Booli Pro för att få en övergripande bild av bostadsmarknaden i Sverige och Stockholm men även för mindre geografiska områden. Jag uppskattar att verktyget uppdateras i realtid och är enkelt att använda för att analysera förutsättningarna för specifika bostadsprojekt. Det underlättar mitt dagliga arbete.',
    name: 'Birgitta Strimbold',
    role: 'Analyschef',
    company: 'HSB BOSTAD',
  },
];
const TestimonialCard = ({ testimonial }: { testimonial: ITestimonial }) => {
  return (
    <div
      className='bg-bui-color-white flex max-w-[40%] min-w-[280px] shrink-0 flex-col justify-between rounded-xl px-8
        py-8 shadow-xl'
    >
      <p className='text-base'>
        <QuoteIcon className='text-bui-color-orange mb-4 h-8' />
        {testimonial.testimonial}
      </p>
      <div className='mt-10 flex flex-col'>
        <p className='font-display text-base'>{`${testimonial.name}`}</p>
        <p className='text-bui-color-gray mt-1 text-sm'>
          {testimonial.role}, <span className='uppercase'>{testimonial.company}</span>
        </p>
      </div>
    </div>
  );
};

const TestimonialsDisplay = () => {
  return (
    <div className='bg-bui-color-peanut relative rounded-lg pt-10 pb-12'>
      <h2 className='font-display text-center opacity-50'>Några av våra fleråriga kunder</h2>
      <ul className='tablet:px-4 mt-4 flex w-full flex-wrap items-center justify-center gap-2'>
        <li className='opacity-50 transition-all duration-300 hover:opacity-100'>
          <BesqabLogotype className='h-6 w-auto' />
        </li>
        <li className='ml-6 opacity-50 transition-all duration-300 hover:opacity-100'>
          <ObosLogotype className='h-6 w-auto -translate-y-0.5' />
        </li>
        <li className='relative ml-4 h-6 w-24 opacity-50 transition-all duration-300 hover:opacity-100'>
          <Image
            src='/images/LandingPage/ClientLogotypes/croisette-logotype.png'
            fill
            style={{ objectFit: 'contain' }}
            alt='Croisette logotyp'
            sizes='(max-height:24px) (max-width:96px)'
          />
        </li>
        <li className='relative ml-3 h-8 w-24 opacity-50 transition-all duration-300 hover:opacity-100'>
          <Image
            src='/images/LandingPage/ClientLogotypes/riksbyggen-logotype.png'
            fill
            style={{ objectFit: 'contain' }}
            alt='Riksbyggen logotyp'
            sizes='(max-height:24px) (max-width:96px)'
          />
        </li>
        <li className='relative ml-3 h-12 w-12 opacity-50 transition-all duration-300 hover:opacity-100'>
          <Image
            src='/images/LandingPage/ClientLogotypes/hsb-logotype.png'
            fill
            style={{ objectFit: 'contain' }}
            alt='HSB logotyp'
            sizes='(max-height:24px) (max-widht:96px)'
          />
        </li>
        <li className='ml-3 opacity-50 transition-all duration-300 hover:opacity-100'>
          <SBABLogotype className='h-6 w-auto' />
        </li>
      </ul>
      <div className='tablet:px-6 px-12'>
        <h2 className='tablet:mt-8 mobile:text-lg mt-10 text-xl'>Testimonials</h2>
        <p className='font-display tablet:text-3xl mobile:text-2xl mt-1 text-4xl'>Vad säger våra användare?</p>
      </div>
      <div className='relative'>
        <div
          className='from-bui-color-peanut tablet:w-6 absolute top-0 bottom-0 left-0 z-50 w-10 bg-linear-to-r
            to-transparent'
        />
        <div className='scrollbar-with-margin tablet:px-6 flex max-w-full space-x-4 overflow-x-auto px-12 py-10'>
          {Testimonials.map((testimonial) => {
            return (
              <TestimonialCard
                key={testimonial.name}
                testimonial={testimonial}
              />
            );
          })}
        </div>
        <div
          className='from-bui-color-peanut tablet:w-6 absolute top-0 right-0 bottom-0 z-50 w-10 bg-linear-to-l
            to-transparent'
        />
      </div>
      <p className='tablet:px-6 mobile:justify-start mt-6 flex items-center justify-end px-10 text-sm'>
        <CheckmarkIcon className='mr-1.5' /> Referenserna är verifierade användare av Booli Pro
      </p>
    </div>
  );
};

export default TestimonialsDisplay;
